import { Currency, CurrencyAmount, Price, Token, TradeType } from '@uniswap/sdk-core'
import { useMemo } from 'react'
import { PollingInterval } from 'uniswap/src/constants/misc'
import { useAccountMeta } from 'uniswap/src/contexts/UniswapContext'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { useCurrencyInfo } from 'uniswap/src/features/tokens/useCurrencyInfo'
import { useTrade } from 'uniswap/src/features/transactions/swap/hooks/useTrade'
import { isClassic } from 'uniswap/src/features/transactions/swap/utils/routing'
import { areCurrencyIdsEqual, buildCurrencyId, currencyId } from 'uniswap/src/utils/currencyId'

// USDC 地址映射（从接口获取的正确地址）
const USDC_ADDRESSES: { [chainId: number]: string } = {
  [UniverseChainId.H2]: '0xd5d9632695346a7e3656478e83603ef1dcd7771e',
}

// Stablecoin amounts used when calculating spot price for a given currency.
// The amount is large enough to filter low liquidity pairs.
// 注意：USDC 代币信息将从接口动态获取
function getStablecoinAmountOut(chainId: number, usdcToken?: Token): CurrencyAmount<Token> | undefined {
  if (!usdcToken) {
    return undefined
  }
  return CurrencyAmount.fromRawAmount(usdcToken, 10_000e6)
}

// 为了向后兼容，导出一个空对象（已弃用）
export const STABLECOIN_AMOUNT_OUT: { [chainId: number]: CurrencyAmount<Token> | undefined } = {
  [UniverseChainId.H2]: undefined, // 动态获取
}

/**
 * Returns the price in USDC of the input currency
 * @param currency currency to compute the USDC price of
 * @param customAmount optional custom amount to use for price calculation (if not provided, uses default STABLECOIN_AMOUNT_OUT)
 */
export function useUSDCPrice(
  currency?: Currency, 
  customAmount?: CurrencyAmount<Currency>
): {
  price: Price<Currency, Currency> | undefined
  isLoading: boolean
} {
  const chainId = currency?.chainId
  const account = useAccountMeta() // ✅ Get current wallet account

  // 🔄 从接口动态获取 USDC 代币信息
  const usdcAddress = chainId ? USDC_ADDRESSES[chainId] : undefined
  const usdcCurrencyId = usdcAddress && chainId ? buildCurrencyId(chainId, usdcAddress) : undefined
  const usdcCurrencyInfo = useCurrencyInfo(usdcCurrencyId)
  const usdcToken = usdcCurrencyInfo?.currency as Token | undefined

  const defaultQuoteAmount = chainId && usdcToken ? getStablecoinAmountOut(chainId, usdcToken) : undefined
  const stablecoin = defaultQuoteAmount?.currency

  // avoid requesting quotes for stablecoin input
  const currencyIsStablecoin = Boolean(
    stablecoin && currency && areCurrencyIdsEqual(currencyId(currency), currencyId(stablecoin)),
  )
  
  // 🎯 Use custom amount if provided and not a stablecoin, otherwise use default
  const amountSpecified = currencyIsStablecoin ? undefined : (customAmount || defaultQuoteAmount)

  // 🐛 Debug: Check useTrade parameters

  const { trade, isLoading } = useTrade({
    account, // ✅ Pass wallet account for correct recipient
    amountSpecified,
    otherCurrency: currency,
    tradeType: customAmount ? TradeType.EXACT_INPUT : TradeType.EXACT_OUTPUT,
    pollInterval: PollingInterval.Fast,
    isUSDQuote: true,
  })

  return useMemo(() => {
    if (!stablecoin) {
      return { price: undefined, isLoading: false }
    }

    if (currencyIsStablecoin) {
      // handle stablecoin
      return { price: new Price(stablecoin, stablecoin, '1', '1'), isLoading: false }
    }

    if (!trade || !isClassic(trade) || !trade.routes?.[0] || !amountSpecified || !currency) {
      return { price: undefined, isLoading }
    }

    const { numerator, denominator } = trade.routes[0].midPrice
    return { price: new Price(currency, stablecoin, denominator, numerator), isLoading }
  }, [currency, stablecoin, currencyIsStablecoin, amountSpecified, trade, isLoading])
}

export function useUSDCValue(
  currencyAmount: CurrencyAmount<Currency> | undefined | null,
): CurrencyAmount<Currency> | null {
  // 🐛 Debug: Check if useUSDCValue is being called
  
  // 🎯 Pass the actual user amount to get accurate price for that specific amount
  const { price } = useUSDCPrice(currencyAmount?.currency, currencyAmount || undefined)

  return useMemo(() => {
    if (!price || !currencyAmount) {
      return null
    }
    try {
      return price.quote(currencyAmount)
    } catch (error) {
      return null
    }
  }, [currencyAmount, price])
}

/**
 * @param currencyAmount
 * @returns Returns fiat value of the currency amount, and loading status of the currency<->stable quote
 */
export function useUSDCValueWithStatus(currencyAmount: CurrencyAmount<Currency> | undefined | null): {
  value: CurrencyAmount<Currency> | null
  isLoading: boolean
} {
  // 🎯 Pass the actual user amount to get accurate price for that specific amount
  const { price, isLoading } = useUSDCPrice(currencyAmount?.currency, currencyAmount || undefined)

  return useMemo(() => {
    if (!price || !currencyAmount) {
      return { value: null, isLoading }
    }
    try {
      return { value: price.quote(currencyAmount), isLoading }
    } catch (error) {
      return {
        value: null,
        isLoading: false,
      }
    }
  }, [currencyAmount, isLoading, price])
}
